<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	//Services
	import { location } from 'svelte-spa-router';
	import { format } from 'svelte-i18n';
	import { postReq } from '../../../services/http';
	import { validate_email } from 'src/helpers/validators';
	//Helpers
	import { real_id } from '../../../helpers/interface_helpers';
	import { deleteSharedDevice } from '../../../helpers/add_delete_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	//Types
	import type { sharedToList } from '../../../types/requests/services/get_shared';
	//Design system comoponents
	import { Button, Input, Loader, Text, Alert, toast } from '@Shelly/shelly-design-system';
	//Components
	import SharedList from './SharedList.svelte';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { devices_config } from '@devices/index';
	import { home_data } from 'src/stores/account/user';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device;
	export let useNewLayout = false;

	let users = [];
	let email;
	let loading = false;
	let invalidEmail = false;
	let loadingBtns = {};

	let deviceName;

	$: isShared = device?.shared;

	onMount(async () => {
		deviceName = device?.owner ? device?.owner : device?.name?.split('from')[1];

		if (isShared) return;

		loading = true;
		const res = (await postReq(`/device/get_shared`, {
			id: real_id(device.id),
			channel: device.channel,
		})) as sharedToList;
		if (res.isok) {
			const data = await res.data;
			users = data.users;
		} else {
			toast.open({ type: 'danger', title: $format('error_global') });
		}
		loading = false;
	});

	const handle_delete = async (e) => {
		loadingBtns[e.detail] = true;

		const res = await postReq(`/device/delete_shared`, {
			id: device.id,
			email: e.detail,
		});

		if (res.isok) {
			toast.open({
				type: 'success',
				title:
					$format('shared_device_remove_user', {
						values: { email: e.detail },
					}) || `You successfully removed ${e.detail} from this device.`,
			});

			users = users.filter((user) => user !== e.detail);
			delete loadingBtns[e.detail];
		} else {
			loadingBtns[e.detail] = false;
			return toast.open({ type: 'danger', title: $format('error_global') });
		}
	};

	const sent_share = async () => {
		let isValidEmail = validate_email(email);

		if (email < 1 || !isValidEmail) {
			invalidEmail = true;
			return;
		}

		const res = await postReq(`/device/add_shared`, {
			id: real_id(device.id),
			channel: device.channel,
			email: email,
		});

		if (res.isok) {
			users.push(email);
			users = users;
			toast.open({
				type: 'success',
				title:
					$format('share_device_with', { values: { email } }) ||
					`You successfully shared this device with ${email}`,
			});
			email = '';
		} else {
			if (res.errors.add_failed) {
				toast.open({
					type: 'danger',
					title: $format('device_already_shared', {
						default: res.errors.add_failed,
					}),
				});
			} else if (res.errors.wrong_recipient) {
				toast.open({
					type: 'danger',
					title: $format('email_does_not_exist', {
						default: res.errors.wrong_recipient,
					}),
				});
			}
			email = '';
		}
	};

	const handleDeleteSharedDevice = async () => {
		const type = $location.split('/')[1];
		const id = type === 'room' ? $location.split('/')[3] : '-1';

		setSidePanelState({
			open: false,
			type,
			id,
		});
		await deleteSharedDevice(device.id);
	};
</script>

{#if isShared}
	{#if devices_config[device?.type]?.isPM && !$home_data?.includeSharedPmEmEnergy?.enable}
		<Alert
			class="mb-s"
			type="warning"
			heading={$format('shared_pm_warning_alert_text', {
				default:
					'Shared PM/EM devices are not included in total energy calculations. Data is available only for the shared device itself.',
			})}
		/>
	{/if}

	<Alert
		heading={$format('shared_device_title', {
			default: 'This device is shared to you by:',
		})}
		message={deviceName}
		type="info"
	/>

	<Button
		width="full"
		class={showNewLayout ? 'mt-m' : 'mt-s'}
		variant="primary"
		size="m"
		on:click={handleDeleteSharedDevice}
	>
		{$format('remove_from_my_acc', { default: 'Remove from my account' })}
	</Button>
{:else if loading}
	<div class="d-flex justify-content-center">
		<Loader />
	</div>
{:else}
	{#if !$showNewLayout}
		{#if users.length === 0}
			<Alert
				class="mb-m"
				heading={$format('alert_heading_not_currently_sharing', {
					default: 'You are not currently sharing this device with another user.',
				})}
				type={'info'}
			/>
			<!-- {#if DEVICES_WITH_MODE_CHANGE.includes(device.type)}
				<Alert
					class="mb-m"
					heading={$format('alert_share_device_mode_change', {
						default:
							'Switching the working mode will remove this device from other accounts.'
					})}
					type={'info'}
				/>
			{/if} -->
		{/if}
		{#if useNewLayout}
			<Text class="mb-s" element="h2"
				>{$format('share_device_title', { default: 'Share this device' })}</Text
			>
		{/if}
	{/if}

	<Text class="mb-s" styleType="body-small">
		{$format('share_device_with_description', {
			default: 'Enter e-mail address of Shelly user:',
		})}</Text
	>
	<Input
		aria-labelledby="input-8rzsag0"
		size="m"
		type="text"
		inputmode={'email'}
		autocomplete="off"
		label={capFirstChar($format('user_email', { default: 'user email' }))}
		error={invalidEmail
			? $format('input_error_valid_email', {
					default: 'Please enter a valid e-mail.',
				})
			: null}
		on:enter={sent_share}
		on:focus={() => {
			invalidEmail = false;
		}}
		bind:value={email}
		id="input-8rzsag0"
	/>

	<Button class="mt-s" width="full" variant="primary" size="m" on:click={sent_share}>
		{$format('button_share', {
			default: 'Share',
		})}
	</Button>

	{#if $showNewLayout}
		<hr class="mt-l mb-l" />
		{#if users.length > 0}
			<Text class="mb-s" element="h2"
				>{$format('shared_device_with_user', {
					default: 'This device is shared with:',
				})}</Text
			>
			<SharedList list={users} on:delete={handle_delete} {loadingBtns} />
		{:else}
			<Alert
				class="mb-m"
				heading={$format('alert_heading_not_currently_sharing', {
					default: 'You are not currently sharing this device with another user.',
				})}
				type={'info'}
			/>
		{/if}
	{:else if users.length > 0}
		<hr class="mt-l mb-l" />
		<Text class="mb-s" element="h2"
			>{$format('shared_device_with_user', {
				default: 'This device is shared with:',
			})}</Text
		>
		<SharedList list={users} on:delete={handle_delete} {loadingBtns} />
	{/if}
{/if}
